import { TrashIcon } from '@diligentcorp/atlas-react-icons';
import {
  ChevronDown,
  ChevronRight,
  ExtendedTypography,
  ExtendedTypographyProps,
  StyledAccordion,
  StyledAccordionDetails,
} from '@issuemanager/apps/client/components';
import { useUserDateRenderer } from '@issuemanager/apps/client/hooks';
import { useAtlasSemanticThemeTokens } from '@issuemanager/apps/client/theme';
import {
  ISSUE_WORKFLOW_EXECUTION_TYPE,
  IssueWorkflowExecutionType,
} from '@issuemanager/constants';
import { DragIndicator } from '@mui/icons-material';
import { Button, ButtonBase, Divider, Stack, styled } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionFieldReturnType } from '../../hooks/use-manage-issue-form';

export type ActionFormValue = {
  title: string;
  dueDate?: Date | number | null;
};

type Props = {
  field: ActionFieldReturnType;
  index: number;
  executionType: IssueWorkflowExecutionType;
  actionFormValue: ActionFormValue;
  actionsCount: number;
  isDragging?: boolean;
  isDragDisabled?: boolean;
};

const StyledButtonBase = styled(ButtonBase)<{
  isFirst?: boolean;
  isDragDisabled?: boolean;
}>(({ isFirst, isDragDisabled }) => ({
  flex: 1,
  font: 'unset',
  fontSize: '1.125em',
  padding: '16px 20px',
  paddingLeft: isDragDisabled ? '20px' : 0,
  borderRadius: isFirst ? '0 4px 0 0' : 0,
  ':focus': {
    boxShadow: '0 0 0 2px #3e95fa',
  },
}));

const StyledSpan = styled('span')(({ theme }) => ({
  display: 'block',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
}));

const WrappedExtendedTypography = styled(
  ExtendedTypography,
)<ExtendedTypographyProps>(() => ({
  overflowWrap: 'break-word',
  hyphens: 'auto',
}));

export default function ActionGroup({
  field,
  index,
  executionType,
  actionFormValue,
  isDragging,
  isDragDisabled,
  actionsCount,
}: Props): JSX.Element {
  const { t } = useTranslation(['action']);
  const semantic = useAtlasSemanticThemeTokens();
  const { renderDate } = useUserDateRenderer();

  const isExpanded = isDragging ? false : field.expanded;

  const canDeleteAction = (
    actionField: {
      readOnly: boolean;
      isInProgress: boolean;
      isToBeApproved: boolean;
      isOpen: boolean;
    },
    count: number,
  ): boolean =>
    !actionField.readOnly &&
    !actionField.isInProgress &&
    !actionField.isToBeApproved &&
    !(actionField.isOpen && count === 1);

  const renderTitle = (): JSX.Element => {
    const titleText = actionFormValue.title || t('form.sections.action');
    const shouldShowDate =
      actionFormValue.dueDate &&
      typeof actionFormValue.dueDate !== 'string' &&
      typeof actionFormValue.dueDate !== 'number';

    const titleDueDate = (
      <ExtendedTypography
        variant='titleSm'
        color={
          field.errors?.dueDate
            ? semantic.color.status.error.value
            : semantic.color.text.body.value
        }
        fontWeight={semantic.fontWeight.emphasis.value}
      >
        {shouldShowDate ? renderDate(actionFormValue.dueDate) : ''}
      </ExtendedTypography>
    );

    return executionType === ISSUE_WORKFLOW_EXECUTION_TYPE.PARALLEL ? (
      <>
        {titleDueDate}
        {shouldShowDate ? <StyledSpan>–</StyledSpan> : null}
        <WrappedExtendedTypography variant='titleSm'>
          {titleText}
        </WrappedExtendedTypography>
      </>
    ) : (
      <>
        <ExtendedTypography variant='titleSm' fontWeight='600'>
          {`${index + 1}.`}
        </ExtendedTypography>
        <StyledSpan>–</StyledSpan>
        {titleDueDate}
        {shouldShowDate ? <StyledSpan>–</StyledSpan> : null}
        <WrappedExtendedTypography variant='titleSm'>
          {titleText}
        </WrappedExtendedTypography>
      </>
    );
  };

  return (
    <StyledAccordion
      data-atlas-alignment='end'
      key={field.id}
      data-testid='action'
      data-index={index}
      expanded={isExpanded}
      ref={field.ref}
      slotProps={{
        transition: { unmountOnExit: false, exit: !isDragging },
      }}
    >
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        borderBottom={`1px solid ${semantic.color.ui.divider.value}`}
      >
        {!isDragDisabled && (
          <DragIndicator sx={{ color: semantic.color.placeholder.value }} />
        )}

        <StyledButtonBase
          aria-controls={`panel${field.id}-content`}
          id={`panel${field.id}-header`}
          isFirst={index === 0}
          isDragDisabled={isDragDisabled}
          onClick={field.handleExpand}
        >
          <Stack direction='row' flex={1} alignItems='center'>
            <Stack marginRight={1}>
              {isExpanded ? <ChevronDown /> : <ChevronRight />}
            </Stack>
            {renderTitle()}
          </Stack>
        </StyledButtonBase>
      </Stack>

      <StyledAccordionDetails
        sx={{
          p: 2.5,
          borderBottom: `1px solid ${semantic.color.ui.divider.value}`,
        }}
      >
        <Stack direction='row' width='100%' spacing={4} mb={1.5}>
          <Stack spacing={3} flex={1}>
            {field.title}
            {field.description}
          </Stack>
          <Divider orientation='vertical' flexItem />
          <Stack spacing={3} flex={1}>
            {field.dueDate}
            {field.ownerId}
            {field.approverId}
          </Stack>
        </Stack>
        {field.attachments}
        {canDeleteAction(field, actionsCount) && (
          <Stack direction='row' justifyContent='flex-end' mt={2}>
            <Button
              onClick={field.handleDelete}
              data-testid='delete'
              startIcon={<TrashIcon />}
            >
              {t('action:actions.delete')}
            </Button>
          </Stack>
        )}
      </StyledAccordionDetails>
    </StyledAccordion>
  );
}
